import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getConsent, setConsent, OPEN_CONSENT_EVENT } from '../lib/consent';

// Self-contained pill buttons (web has no shared Button). Styled with the
// --brand-driven tokens so they pick up the site's orange accent.
const buttonBase =
  'inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-label-bold ' +
  'font-display font-bold tracking-wide transition-all duration-300 active:scale-95';
const primaryButton =
  `${buttonBase} bg-primary-container text-on-primary-container shadow-glow-sm ` +
  'hover:shadow-glow-orange hover:scale-[1.04]';
const ghostButton =
  `${buttonBase} border border-glass-border bg-white/5 text-on-background ` +
  'hover:border-primary-container/60 hover:bg-white/[0.08] hover:scale-[1.03]';

/**
 * Cookie-consent banner (Google Consent Mode v2). Renders client-side only —
 * it starts hidden, then shows on mount if the visitor hasn't chosen yet. The
 * footer "Cookie settings" control re-opens it via OPEN_CONSENT_EVENT.
 */
export function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    // Skip automated contexts (e.g. crawlers set navigator.webdriver).
    if (getConsent() === null && !navigator.webdriver) setOpen(true);
    const reopen = () => setOpen(true);
    window.addEventListener(OPEN_CONSENT_EVENT, reopen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, reopen);
  }, []);

  // Trigger the slide-in on the frame after mount (so the transition runs).
  useEffect(() => {
    if (!open) {
      setShown(false);
      return;
    }
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  if (!open) return null;

  const choose = (choice: 'granted' | 'denied') => {
    setConsent(choice);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className={[
        'fixed inset-x-0 bottom-0 z-50 px-margin-safe pb-4 transition-all duration-400 motion-reduce:transition-none',
        shown ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
      ].join(' ')}
    >
      <div className="container-byaft">
        <div className="mx-auto flex max-w-3xl flex-col gap-4 rounded-2xl border border-glass-border bg-surface/95 p-5 shadow-glow-sm backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <p className="text-sm text-on-surface-variant">
            We use analytics cookies to understand how BYAFT is used — nothing else, no ads.{' '}
            <Link to="/privacy" className="text-primary-container underline-offset-2 hover:underline">
              Learn more
            </Link>
            .
          </p>
          <div className="flex shrink-0 gap-2">
            <button type="button" onClick={() => choose('denied')} className={ghostButton}>
              Decline
            </button>
            <button type="button" onClick={() => choose('granted')} className={primaryButton}>
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
